import { memo, Suspense, useMemo, type ReactElement } from 'react';

import Fallback from 'shared/ui/Fallback';

import GroupedList, {
  getDefaultGroupState,
  getDefaultRowState,
  GetFormFunction,
  getGroupedListClassNames,
  useGroupRowStates,
  useRowStates,
  useSorter,
  useTableState,
  type RowState,
  type GroupRowState,
  type ControlDescriptor,
  type UpdateAndSortAction,
  type BaseGroupedListProps,
} from 'components/GroupedList';
import { ListedResponse } from 'types/common-types';

import GroupedTableHeader from './GroupedTableHeader';

import './GroupedTable.scss';

export interface GroupedTableProps<G, I> extends BaseGroupedListProps<G, I> {
  itemIdKey: string;
  groupIdKey: string;
  itemsData: ListedResponse<I>;
  groupsData: ListedResponse<G>;
  itemEntityType: string;
  getItemForm: GetFormFunction<I, RowState>;
  getGroupForm: GetFormFunction<G, GroupRowState>;
  headerIcon: ReactElement;
  headerText: ReactElement | string;
  tableControls?: ControlDescriptor<I, G>[];
  updateAndSort?: UpdateAndSortAction<I>;
}

const GroupedTable = <G, I>(props: GroupedTableProps<G, I>) => {
  const {
    dndEnabled,
    itemIdKey,
    groupIdKey,
    itemsData,
    groupsData,
    itemControls,
    groupControls,
    tableControls,
    itemEntityType,
    headerIcon,
    headerText,
    classNames: classNamesFromProps,
    getItems,
    getGroups,
    getItemId,
    getGroupId,
    getItemLabel,
    getItemGroupId,
    getGroupLabel,
    getItemForm,
    getGroupForm,
    isItemSortable,
    getItemPlaceholder,
    updateAndSort,
  } = props;

  const classNames = useMemo(() => getGroupedListClassNames(classNamesFromProps), [classNamesFromProps]);

  const groupsWithEmpty = useMemo(() => [{}, ...groupsData.items], [groupsData.items]);

  const { tableState, updateTableState } = useTableState();

  const { rowStates, updateRowState } = useRowStates(itemsData.items, getItemId, getDefaultRowState);

  const { groupStates, expand, collapse, expandAll, collapseAll, updateGroupState } = useGroupRowStates(
    groupsWithEmpty,
    getGroupId,
    getDefaultGroupState,
  );

  const { onApplySort } = useSorter({
    data: itemsData,
    entityIdKey: itemIdKey,
    entityGroupIdKey: groupIdKey,
    entityCacheName: itemEntityType,
    updateAndSort,
  });

  return (
    <>
      <div className="margin-bottom">
        <GroupedTableHeader
          icon={headerIcon}
          text={headerText}
          classNames={classNames}
          controls={tableControls}
          tableState={tableState}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}
          updateTableState={updateTableState}
        />
      </div>

      <Suspense fallback={<Fallback />}>
        {tableState.regularForm && <div className="gt-table__regular-form">{tableState.regularForm}</div>}

        <GroupedList
          dndEnabled={dndEnabled}
          items={itemsData.items}
          classNames={classNames}
          itemStates={rowStates}
          groupStates={groupStates}
          groups={groupsData.items}
          itemControls={itemControls}
          groupControls={groupControls}
          getItems={getItems}
          getGroups={getGroups}
          getItemId={getItemId}
          getGroupId={getGroupId}
          getItemLabel={getItemLabel}
          getGroupLabel={getGroupLabel}
          getItemGroupId={getItemGroupId}
          getItemForm={getItemForm}
          getGroupForm={getGroupForm}
          isItemSortable={isItemSortable}
          getItemPlaceholder={getItemPlaceholder}
          onExpand={expand}
          onCollapse={collapse}
          onRearrange={onApplySort}
          updateItemState={updateRowState}
          updateGroupState={updateGroupState}
        />
      </Suspense>
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(GroupedTable);
